<template>
  <main class="kyc-result-page">
    <AppNavBar
      :title="t('card.kycReview')"
      :auto-back="false"
      @click-left="goBackHome"
    />

    <section class="result-card">
      <div class="result-card__icon">
        <AppSvgIcon
          v-if="statusInfo.iconName"
          :name="statusInfo.iconName"
          size="136"
        />
        <app-image v-else :src="statusInfo.icon" alt="" />
      </div>

      <h1>{{ statusInfo.title }}</h1>
      <p v-if="statusInfo.tip">{{ statusInfo.tip }}</p>

      <div v-if="isRejected && rejectReason" class="reason-box">
        <div class="reason-box__title">
          <AppSvgIcon name="reason" size="18" />
          <div data-strong>{{ t("card.failedReason") }}</div>
        </div>
        <span>{{ rejectReason }}</span>
      </div>

      <div class="action-group">
        <van-button block round type="primary" @click="handleNext">
          {{ statusInfo.button }}
        </van-button>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useCardKycStore } from "@/stores/card-kyc";
import { getRouteState, navigateWithState } from "@/utils/route-state";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const cardKycStore = useCardKycStore();
const pageState = computed(() => getRouteState(route, "card-kyc-result", {}));

const isPhysicalFlow = computed(
  () => String(pageState.value.type || "") === "1",
);
const holderStatus = computed(() =>
  String(pageState.value.holderStatus || cardKycStore.holderStatus || "2"),
);
const rejectReason = computed(() =>
  String(pageState.value.reason || cardKycStore.rejectReason || ""),
);
const isRejected = computed(
  () => holderStatus.value === "1" || holderStatus.value === "4",
);
const isPending = computed(() => holderStatus.value === "2");
const isApproved = computed(() => holderStatus.value === "3");

const statusInfo = computed(() => {
  if (isRejected.value) {
    return {
      icon: "",
      iconName: "reject_icon",
      title: t("card.kycRejected"),
      tip: "",
      button: t("card.resubmit"),
    };
  }

  if (isApproved.value) {
    return {
      icon: "",
      iconName: "approve_icon",
      title: t("card.kycApproved"),
      tip: t("card.cardCanOpen"),
      button: t("card.GoOpenCard"),
    };
  }

  return {
    icon: "",
    iconName: "approve_icon",
    title: t("card.kycPending"),
    tip: t("card.reviewTip"),
    button: t("card.reviewDone"),
  };
});

function goBackHome() {
  if (isPhysicalFlow.value) {
    navigateWithState(router, "/home/card", {
      namespace: "home-card",
      replace: true,
      payload: { tab: "physical" },
    });
    return;
  }
  router.replace("/home/card");
}

function handleNext() {
  if (isRejected.value) {
    router.replace("/card/kyc");
    return;
  }
  if (isApproved.value) {
    navigateWithState(router, "/home/card", {
      namespace: "home-card",
      replace: true,
      payload: { type: 2 },
    });
    return;
  }
  router.replace("/home/index");
}
</script>

<style scoped lang="scss">
.kyc-result-page {
  min-height: var(--app-viewport-height);
  padding: 0 20px 36px;
  background: #ffffff;
}

.result-card {
  padding: 40px 0 0;
  text-align: center;

  &__icon {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;

    .app-image,
    .app-svg-icon {
      width: 136px;
      height: 136px;
    }
  }

  h1 {
    margin: 0;
    color: #000000;
    font-size: 18px;
    font-weight: 500;
    line-height: 26px;
  }

  p {
    margin: 5px 0 0;
    color: #666666;
    font-size: 15px;
    // line-height: 22px;
  }
}

.reason-box {
  // margin-top: 24px;
  padding: 21px 20px 16px;
  border: 1px solid var(--wp-color-card-1);
  border-radius: 16px;
  background: var(--wp-color-card-3);
  text-align: center;

  &__title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;

    .app-image {
      width: 20px;
      height: 20px;
      object-fit: contain;
    }
  }

  [data-strong] {
    color: var(--wp-color-brand-1);
    font-size: 18px;
    font-weight: 500;
  }

  span {
    display: block;
    margin-top: 10px;
    color: #666666;
    font-size: 15px;
    line-height: 22px;
    word-break: break-word;
  }
}

.action-group {
  margin-top: 40px;
}
</style>
